/**
 * `/` — the department picker (SPEC §9.1).
 *
 * Tiles for accessible departments only. A user with no grants is redirected
 * to `/no-access`, which names who to contact, rather than being shown an
 * empty grid they might read as "the portal is broken".
 */

import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { Link, Navigate } from 'react-router'

import { api } from '../lib/api'
import { ErrorState, Skeleton } from '../components/States'

export function Departments() {
  const { data, error, isPending } = useQuery({
    queryKey: ['departments'],
    queryFn: api.departments,
  })

  if (isPending) return <Skeleton rows={4} />
  if (error !== null) return <ErrorState error={error} />
  if (data.length === 0) return <Navigate to="/no-access" replace />

  return (
    <div>
      <h1 className="text-[length:var(--text-lg)] font-semibold tracking-tight">Departments</h1>
      <p className="mt-1 text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
        Reports you have access to, grouped by the team accountable for them.
      </p>

      <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((department) => (
          <li key={department.key}>
            <Link
              to={`/d/${department.key}`}
              className="group flex items-start gap-3 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-4 elevated hover:border-[var(--color-ink-muted)]"
            >
              {/* The tile's accent bar — the same wayfinding as the rail. */}
              <span
                aria-hidden
                className="mt-0.5 h-10 w-1 shrink-0 rounded-full"
                style={{ background: department.accent }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[length:var(--text-base)] font-medium">
                  {department.name}
                </span>
                <span className="mt-0.5 block text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
                  <span className="numeric">{department.report_count}</span>{' '}
                  {department.report_count === 1 ? 'report' : 'reports'}
                </span>
              </span>
              <ArrowRight
                aria-hidden
                className="size-4 shrink-0 text-[var(--color-ink-muted)] group-hover:text-[var(--color-ink)]"
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
